/**
 * Volunteer Summary Widget - Compact sidebar widget for HOS Dashboard
 * 
 * Shows at-a-glance volunteer status with link to full management page.
 */

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Users, Clock, AlertTriangle, ChevronRight, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function VolunteerSummaryWidget({ isDark, compact = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("hospice_token");
        const res = await fetch(`${API_URL}/api/volunteer-management/dashboard`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        if (res.ok) {
          const result = await res.json();
          setData(result);
        }
      } catch (error) {
        console.error("Error fetching volunteer dashboard:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className={`p-3 rounded-lg ${isDark ? "bg-slate-800/50" : "bg-gray-100"}`}>
        <div className="flex items-center justify-center py-2">
          <Loader2 className="w-4 h-4 animate-spin text-teal-500" />
        </div>
      </div>
    );
  }

  const activeVolunteers = data?.summary?.active_volunteers || 0;
  const monthlyHours = data?.visits?.monthly_hours || 0;
  const missedVisits = data?.visits?.missed_visits || 0;
  const complianceRate = data?.compliance?.compliance_rate || 0;

  if (compact) {
    return (
      <div 
        className={`p-3 rounded-lg cursor-pointer transition-colors ${
          isDark 
            ? "bg-slate-800/50 hover:bg-slate-800" 
            : "bg-gray-100 hover:bg-gray-200"
        }`}
        onClick={() => navigate("/volunteer-management")}
        data-testid="volunteer-summary-widget"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-teal-500" />
            <span className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
              Volunteers
            </span>
          </div>
          <ChevronRight className={`w-4 h-4 ${isDark ? "text-slate-500" : "text-gray-400"}`} />
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-center">
              <p className="text-lg font-bold text-teal-500">{activeVolunteers}</p>
              <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Active</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-blue-500">{monthlyHours}h</p>
              <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Month</p>
            </div>
          </div>
          {missedVisits > 0 && (
            <Badge className="bg-red-500/20 text-red-400 border-0 text-xs">
              {missedVisits} missed
            </Badge>
          )}
        </div>
      </div>
    );
  }

  return (
    <Card 
      className={`cursor-pointer transition-colors ${
        isDark 
          ? "bg-slate-900 border-slate-800 hover:border-teal-500/50" 
          : "bg-white hover:border-teal-500"
      }`}
      onClick={() => navigate("/volunteer-management")}
      data-testid="volunteer-summary-widget-full"
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-teal-500/20 flex items-center justify-center">
              <Users className="w-4 h-4 text-teal-500" />
            </div>
            <span className={`font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
              Volunteer Management
            </span>
          </div>
          <ChevronRight className={`w-5 h-5 ${isDark ? "text-slate-500" : "text-gray-400"}`} />
        </div>

        <div className="grid grid-cols-3 gap-3 mb-3">
          <div className="text-center">
            <p className="text-xl font-bold text-teal-500">{activeVolunteers}</p>
            <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Active</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-blue-500">{monthlyHours}h</p>
            <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Monthly Hours</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-purple-500">{data?.summary?.active_assignments || 0}</p>
            <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Assignments</p>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>
              Compliance Rate
            </span>
            <span className="text-xs font-medium">{complianceRate}%</span>
          </div>
          <Progress value={complianceRate} className="h-1.5" />
        </div>

        {missedVisits > 0 && (
          <div className={`mt-3 p-2 rounded-lg flex items-center gap-2 ${
            isDark ? "bg-red-500/10" : "bg-red-50"
          }`}>
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <span className="text-xs text-red-500 font-medium">
              {missedVisits} missed visits need attention
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default VolunteerSummaryWidget;
